<template>
  <section class="rule-section" aria-label="加分项评分标准">
    <div class="section-toolbar">
      <div class="cap-summary">
        <span>维度数 <strong>{{ dimensionCount }}</strong></span>
        <span>合计满分 <strong>{{ totalCap }}</strong></span>
      </div>
      <el-button
        type="primary"
        icon="el-icon-check"
        :loading="saving"
        :disabled="!hasChanges() || loading"
        @click="save"
      >保存加分标准</el-button>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon :closable="false">
      <el-button slot="default" size="mini" @click="load">重新加载</el-button>
    </el-alert>

    <el-table
      v-else
      v-loading="loading"
      :data="rows"
      :span-method="spanFixedColumns"
      border
      stripe
      empty-text="暂无加分项评分标准"
    >
      <el-table-column prop="dimensionName" label="加分维度" min-width="170" />
      <el-table-column prop="scoreItem" label="评分项" min-width="240" />
      <el-table-column prop="ruleText" label="固定规则" min-width="190" />
      <el-table-column label="规则分值" width="190" align="center">
        <template slot-scope="scope">
          <el-input-number class="rule-score-input" v-model="scope.row.score"
            :min="0" :precision="2" :step="1" />
        </template>
      </el-table-column>
      <el-table-column label="维度封顶分" width="200" align="center">
        <template slot-scope="scope">
          <el-input-number
            class="dimension-cap-input"
            v-model="scope.row.dimensionCap"
            :min="0"
            :precision="2"
            :step="1"
            @change="value => syncDimensionCap(scope.row.dimensionCode, value)"
          />
        </template>
      </el-table-column>
      <el-table-column label="备注" min-width="180">
        <template slot-scope="scope">
          <el-input v-model.trim="scope.row.remark" type="textarea" :autosize="{ minRows: 3, maxRows: 6 }"
            maxlength="255" show-word-limit placeholder="请输入备注"
            @input="value => syncDimensionRemark(scope.row.dimensionCode, value)" />
        </template>
      </el-table-column>
    </el-table>
  </section>
</template>

<script>
const API = 'apij2/admin-api/trade/enterprise-innovation-point-rule';
const BONUS_TOTAL_CAP = 30;

export default {
  name: 'BonusRuleConfig',
  data() {
    return { rows: [], original: '', loading: false, saving: false, error: '' };
  },
  computed: {
    dimensionCount() {
      return new Set(this.rows.map(row => row.dimensionCode)).size;
    },
    totalCap() {
      return this.rows.length ? Number(this.rows[0].totalCap || 0) : 0;
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const res = await this.http.get(`${this.urlConfig.devUrl}${API}/bonus-rules`, true);
        this.rows = (res.data || []).map(row => ({
          ...row,
          score: Number(row.score),
          dimensionCap: Number(row.dimensionCap),
          totalCap: BONUS_TOTAL_CAP,
          remark: row.remark || ''
        }));
        this.original = this.snapshot();
      } catch (error) {
        this.error = (error && (error.msg || error.status)) || '加分项评分标准加载失败';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      const validationMessage = this.validateRows();
      if (validationMessage) {
        this.$message.warning(validationMessage);
        return;
      }
      this.saving = true;
      try {
        await this.http.post(`${this.urlConfig.devUrl}${API}/bonus-rules/save`, {
          items: this.rows.map(({ id, score, dimensionCap, totalCap, remark }) => ({
            id, score, dimensionCap, totalCap, remark
          }))
        }, true);
        this.original = this.snapshot();
        this.$message.success('加分项评分标准已保存');
      } finally {
        this.saving = false;
      }
    },
    validateRows() {
      const caps = {};
      for (const row of this.rows) {
        if ([row.score, row.dimensionCap, row.totalCap].some(value => !Number.isFinite(Number(value)) || Number(value) < 0)) {
          return `${row.dimensionName}存在无效分值或封顶分`;
        }
        if (Number(row.score) > Number(row.dimensionCap)) return `${row.dimensionName}的规则分值不能超过维度封顶分`;
        caps[row.dimensionCode] = Number(row.dimensionCap);
      }
      const capValues = Object.values(caps);
      const sum = capValues.reduce((total, value) => total + value, 0);
      if (this.totalCap !== BONUS_TOTAL_CAP) {
        return '加分项合计满分必须为30分';
      }
      if (Math.abs(sum - BONUS_TOTAL_CAP) > 0.000001) {
        return '各维度封顶分之和必须等于合计满分30分';
      }
      return '';
    },
    syncDimensionCap(dimensionCode, value) {
      this.rows.forEach(row => {
        if (row.dimensionCode === dimensionCode) row.dimensionCap = value;
      });
    },
    syncDimensionRemark(dimensionCode, value) {
      this.rows.forEach(row => {
        if (row.dimensionCode === dimensionCode) row.remark = value;
      });
    },
    snapshot() {
      return JSON.stringify(this.rows.map(row => [
        row.id, row.score, row.dimensionCap, row.totalCap, row.remark
      ]));
    },
    hasChanges() {
      return Boolean(this.original) && this.snapshot() !== this.original;
    },
    spanFixedColumns({ rowIndex, columnIndex }) {
      const row = this.rows[rowIndex];
      const dimensionColumns = [0, 4, 5];
      let sameGroup;
      if (dimensionColumns.includes(columnIndex)) {
        sameGroup = item => item.dimensionCode === row.dimensionCode;
      } else if (columnIndex === 1) {
        sameGroup = item => item.dimensionCode === row.dimensionCode && item.scoreItem === row.scoreItem;
      } else {
        return;
      }
      if (rowIndex > 0 && sameGroup(this.rows[rowIndex - 1])) return [0, 0];
      let rowspan = 1;
      while (this.rows[rowIndex + rowspan] && sameGroup(this.rows[rowIndex + rowspan])) rowspan += 1;
      return [rowspan, 1];
    }
  }
};
</script>

<style lang="scss" scoped>
.section-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.cap-summary {
  display: flex;
  gap: 24px;
  color: #606266;

  strong { margin-left: 6px; color: #303133; font-size: 17px; }
}

::v-deep .el-input-number { width: 122px; }
::v-deep .rule-score-input { width: 150px; }
::v-deep .dimension-cap-input { width: 160px; }
::v-deep .el-alert { margin-bottom: 16px; }
</style>
